import { useState, useEffect, useCallback } from "react";
import { Card, Table, Button, Select, Input, Space, Tag, Row, Col } from "antd";
import {
  EyeOutlined,
  SearchOutlined,
  PercentageOutlined,
} from "@ant-design/icons";
import { Helmet } from "react-helmet";
import Utilities from "../../../dependencies/custom/react-utilities";
import domainSettings from "../../../dependencies/custom/settings";
import { useNavigate, useLocation } from "react-router-dom";
import StudentFinanceDrawer from "../../../components/finance/StudentFinanceDrawer";
import useAcademicYears from "../../../hooks/common/useAcademicYears";

const { Option } = Select;

/**
 * Fee Assignments View Page
 *
 * Shows all students with their fee assignments
 * Click a student to see complete financial breakdown in drawer
 * Supports filtering by class, academic year, user, and discount status
 */
const FeeAssignmentsView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { academicYears, fetchAcademicYears } = useAcademicYears();

  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [classes, setClasses] = useState([]);
  const [users, setUsers] = useState([]);
  const [searchDebounceTimer, setSearchDebounceTimer] = useState(null);

  // Drawer state
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    searchText: "",
    classId: null,
    acadyr: null,
    userId: null,
    hasDiscount: null,
  });

  /**
   * Handle navigation state - open drawer if coming from students page
   */
  useEffect(() => {
    if (location.state?.openDrawerFor) {
      const { spc, firstName, lastName } = location.state.openDrawerFor;
      setSelectedStudent({
        spc,
        spcCode: spc,
        firstName,
        first_name: firstName,
        lastName,
        last_name: lastName,
      });
      setDrawerVisible(true);
      // Clear the state to prevent re-opening on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  /**
   * Fetch classes
   */
  const fetchClasses = useCallback(async () => {
    try {
      const response = await Utilities.fetchWithAuth(
        "GET",
        domainSettings.urls.bootstrap.classes,
        null,
        navigate,
      );
      if (response.ok) {
        // Ensure we always set an array
        const classesData = Array.isArray(response.data) ? response.data : [];
        setClasses(classesData);
      }
    } catch (error) {
      console.error("Error fetching classes:", error);
      setClasses([]); // Set empty array on error
    }
  }, [navigate]);

  /**
   * Fetch users (parent accounts)
   */
  const fetchUsers = useCallback(async () => {
    try {
      const response = await Utilities.fetchWithAuth(
        "GET",
        domainSettings.urls.auth.listUsers,
        null,
        navigate,
      );
      if (response.ok) {
        const usersData = Array.isArray(response.data) ? response.data : [];
        setUsers(usersData);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
    }
  }, [navigate]);

  /**
   * Fetch students with fee assignments
   */
  const fetchStudents = useCallback(
    async (queryFilters = {}) => {
      setLoading(true);
      try {
        // Build query parameters - server-side filters
        const params = [];

        if (queryFilters.acadyr)
          params.push(`acadyr=${encodeURIComponent(queryFilters.acadyr)}`);
        if (queryFilters.className)
          params.push(
            `className=${encodeURIComponent(queryFilters.className)}`,
          );
        if (queryFilters.termId)
          params.push(`termId=${encodeURIComponent(queryFilters.termId)}`);
        if (queryFilters.userId)
          params.push(`user=${encodeURIComponent(queryFilters.userId)}`);
        if (
          queryFilters.hasDiscount !== null &&
          queryFilters.hasDiscount !== undefined
        ) {
          params.push(`has_discount=${queryFilters.hasDiscount}`);
        }

        const queryString = params.length > 0 ? "?" + params.join("&") : "";
        const url = `${domainSettings.urls.enrollment.student}${queryString}`;

        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          // Handle both paginated and non-paginated responses
          const studentsData = response.data?.results || response.data || [];

          // Ensure we always set an array
          const validStudentsData = Array.isArray(studentsData)
            ? studentsData
            : [];
          setStudents(validStudentsData);
        } else {
          setStudents([]);
        }
      } catch (error) {
        console.error("Error fetching students:", error);
        setStudents([]);
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  useEffect(() => {
    fetchClasses();
    fetchAcademicYears();
    fetchUsers();
    fetchStudents();
  }, []);

  /**
   * Handle filter changes - refetch students when filters change
   * Debounces search to avoid spam requests
   * All filters are server-side
   */
  useEffect(() => {
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
    }

    // Build query filters - only include non-null/undefined values
    // All filters are server-side
    const queryFilters = {};
    if (filters.acadyr !== null && filters.acadyr !== undefined)
      queryFilters.acadyr = filters.acadyr;
    if (filters.userId !== null && filters.userId !== undefined)
      queryFilters.userId = filters.userId;
    if (filters.hasDiscount !== null && filters.hasDiscount !== undefined)
      queryFilters.hasDiscount = filters.hasDiscount;

    if (filters.classId !== null && filters.classId !== undefined) {
      const selectedClass = classes.find((c) => c.id === filters.classId);
      if (selectedClass) queryFilters.className = selectedClass.name;
    }

    // Debounce to batch rapid filter changes (500ms)
    const timer = setTimeout(() => {
      // Always refetch - whether filters exist or not (to reset when cleared)
      if (Object.keys(queryFilters).length > 0) {
        fetchStudents(queryFilters);
      } else {
        // Reset to all students when no filters
        fetchStudents();
      }
    }, 500);

    setSearchDebounceTimer(timer);

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [
    filters.acadyr,
    filters.classId,
    filters.userId,
    filters.hasDiscount,
    classes,
    fetchStudents,
  ]);

  /**
   * Filter students on the client side (only for search text)
   */
  const filteredStudents = students.filter((student) => {
    const firstName = student.firstName || student.first_name || "";
    const lastName = student.lastName || student.last_name || "";
    const spc = student.spcCode || student.spc || "";

    // Search filter (name or SPC) - client-side only
    if (filters.searchText) {
      const search = filters.searchText.toLowerCase();
      const fullName = `${firstName} ${lastName}`.toLowerCase();
      if (!fullName.includes(search) && !spc.toLowerCase().includes(search)) {
        return false;
      }
    }

    return true;
  });

  /**
   * Open student finance drawer
   */
  const openStudentDrawer = (student) => {
    setSelectedStudent(student);
    setDrawerVisible(true);
  };

  /**
   * Close drawer
   */
  const closeDrawer = () => {
    setDrawerVisible(false);
    setSelectedStudent(null);
  };

  /**
   * Table columns
   */
  const columns = [
    {
      title: "Student",
      key: "student",
      render: (_, record) => {
        // Handle both camelCase and snake_case field names
        const firstName = record.firstName || record.first_name;
        const lastName = record.lastName || record.last_name;
        const spc = record.spcCode || record.spc;

        return (
          <div>
            <div style={{ fontWeight: 500 }}>
              {firstName} {lastName}
            </div>
            <div style={{ fontSize: "12px", color: "#888" }}>{spc}</div>
          </div>
        );
      },
      width: 200,
    },
    {
      title: "Class",
      key: "class_name",
      render: (_, record) => {
        const className = record.className || record.class_name;
        return className || "-";
      },
      width: 120,
    },
    {
      title: "Current Term",
      key: "current_term",
      render: (_, record) => {
        const currentTerm = record.currentTerm || record.current_term_name;
        return currentTerm || "-";
      },
      width: 120,
    },
    {
      title: "Academic Year",
      key: "acadyr",
      render: (_, record) => {
        const acadyr = record.acadyr || record.current_acadyr;
        return acadyr || "-";
      },
      width: 130,
    },
    {
      title: "Discount",
      key: "hasAnyDiscount",
      render: (_, record) => {
        const hasDiscount = record.hasAnyDiscount;
        return hasDiscount ? (
          <Tag color="orange" icon={<PercentageOutlined />}>
            Has Discount
          </Tag>
        ) : (
          <Tag>No Discount</Tag>
        );
      },
      width: 120,
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => {
        const spc = record.spcCode || record.spc;
        const firstName = record.firstName || record.first_name;
        const lastName = record.lastName || record.last_name;

        return (
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() =>
              openStudentDrawer({
                ...record,
                spc,
                first_name: firstName,
                last_name: lastName,
              })
            }
          >
            View Fees
          </Button>
        );
      },
    },
  ];

  return (
    <>
      <Helmet>
        <title>Fee Assignments | Finance</title>
      </Helmet>

      <div className="content-wrapper">
        <div className="mg-b-20">
          <h4 className="mg-b-5">Student Fee Assignments</h4>
          <p className="tx-color-03 mg-b-0">View all student fee assignments</p>
        </div>

        <Card>
          {/* Filters */}
          <Space direction="vertical" style={{ width: "100%" }} size="large">
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} lg={4}>
                <Input
                  placeholder="Search by name or SPC..."
                  prefix={<SearchOutlined />}
                  value={filters.searchText}
                  onChange={(e) =>
                    setFilters({ ...filters, searchText: e.target.value })
                  }
                  allowClear
                />
              </Col>
              <Col xs={24} sm={12} lg={4}>
                <Select
                  placeholder="Filter by Class"
                  style={{ width: "100%" }}
                  allowClear
                  value={filters.classId}
                  onChange={(value) =>
                    setFilters({ ...filters, classId: value ?? null })
                  }
                >
                  {Array.isArray(classes) &&
                    classes.map((cls) => (
                      <Option key={cls.id} value={cls.id}>
                        {cls.name}
                      </Option>
                    ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} lg={4}>
                <Select
                  placeholder="Filter by Academic Year"
                  style={{ width: "100%" }}
                  allowClear
                  value={filters.acadyr}
                  onChange={(value) =>
                    setFilters({ ...filters, acadyr: value ?? null })
                  }
                >
                  {Array.isArray(academicYears) &&
                    academicYears.map((year) => (
                      <Option key={year.id} value={year.acadyr}>
                        {year.acadyr}
                      </Option>
                    ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} lg={4}>
                <Select
                  placeholder="Filter by User Account"
                  style={{ width: "100%" }}
                  allowClear
                  showSearch
                  value={filters.userId}
                  onChange={(value) =>
                    setFilters({ ...filters, userId: value ?? null })
                  }
                  filterOption={(input, option) => {
                    const user = users.find((u) => u.userID === option.value);
                    if (!user) return false;
                    const searchText = input.toLowerCase();
                    const fullName = (user.fullName || "").toLowerCase();
                    const email = (user.email || "").toLowerCase();
                    return (
                      fullName.includes(searchText) ||
                      email.includes(searchText)
                    );
                  }}
                >
                  {Array.isArray(users) &&
                    users.map((user) => (
                      <Option key={user.userID} value={user.userID}>
                        {user.fullName || user.email}{" "}
                        {user.staffId ? "(Staff)" : ""}
                      </Option>
                    ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} lg={4}>
                <Select
                  placeholder="Discount Status"
                  style={{ width: "100%" }}
                  allowClear
                  value={filters.hasDiscount}
                  onChange={(value) =>
                    setFilters({ ...filters, hasDiscount: value ?? null })
                  }
                >
                  <Option value={true}>Has Discount</Option>
                  <Option value={false}>No Discount</Option>
                </Select>
              </Col>
            </Row>

            {/* Results Summary */}
            {(filters.searchText ||
              filters.classId ||
              filters.acadyr ||
              filters.userId ||
              filters.hasDiscount !== null) && (
              <div>
                <Tag color="blue">
                  Showing {filteredStudents.length} of {students.length}{" "}
                  student(s)
                </Tag>
              </div>
            )}

            {/* Students Table */}
            <Table
              columns={columns}
              dataSource={filteredStudents}
              rowKey={(record) => record.spcCode || record.spc || record.id}
              loading={loading}
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} student(s)`,
                responsive: true,
              }}
              scroll={{ x: 900 }}
            />
          </Space>
        </Card>

        {/* Student Finance Drawer */}
        <StudentFinanceDrawer
          visible={drawerVisible}
          onClose={closeDrawer}
          studentSpc={selectedStudent?.spc || selectedStudent?.spcCode}
          studentName={
            selectedStudent
              ? `${selectedStudent.first_name || selectedStudent.firstName} ${selectedStudent.last_name || selectedStudent.lastName}`
              : ""
          }
        />
      </div>
    </>
  );
};

export default FeeAssignmentsView;
